import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Link2, MessageCircle, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-KE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/articles/${id}`)
      .then((r) => {
        setArticle(r.data);
        document.title = `${r.data.title} — Musesya & Company Advocates`;
      })
      .catch(() => setMissing(true));
    return () => {
      document.title = "Musesya & Company Advocates — Nairobi Law Firm";
    };
  }, [id]);

  const shareUrl = window.location.href;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it anywhere.");
    } catch {
      toast.error("Could not copy. Long-press the address bar instead.");
    }
  };

  if (missing) {
    return (
      <div className="min-h-screen bg-navy-950 text-ink flex flex-col items-center justify-center gap-6 px-6" data-testid="article-not-found">
        <p className="font-display text-2xl">This update is no longer available.</p>
        <Link to="/" data-testid="article-back-link" className="text-gold text-sm hover:underline">
          &larr; Back to the website
        </Link>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-navy-950 flex items-center justify-center" data-testid="article-loading">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">Loading…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 text-ink" data-testid="article-page">
      <div className="grain-overlay" aria-hidden="true" />
      <header className="border-b border-gold/15">
        <div className="max-w-3xl mx-auto px-6 h-[70px] flex items-center justify-between">
          <Link to="/" data-testid="article-brand-link" className="flex items-center gap-3">
            <img src="/assets/logo.jpg" alt="Firm seal" className="h-9 w-9 rounded-full ring-1 ring-gold/50 object-cover" />
            <span className="font-display text-xs tracking-[0.18em] hidden sm:block">
              MUSESYA <span className="text-gold">&</span> CO. ADVOCATES
            </span>
          </Link>
          <Link
            to="/"
            data-testid="article-back-link"
            className="flex items-center gap-2 text-sm text-ink-dim hover:text-gold transition-colors"
          >
            <ArrowLeft size={15} /> Back to website
          </Link>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-3xl mx-auto px-6 py-16"
      >
        <div className="flex flex-wrap items-center gap-3">
          <BookOpen size={15} className="text-gold" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold border border-gold/25 rounded-full px-3 py-1">
            {article.category}
          </span>
          <span className="font-mono text-xs text-ink-muted">{fmtDate(article.created_at)}</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl mt-8 leading-snug" data-testid="article-title">
          {article.title}
        </h1>
        <p className="font-editorial italic text-lg text-ink-dim mt-4">{article.summary}</p>

        <div className="gold-hairline my-10" />

        <div className="space-y-5" data-testid="article-body">
          {article.body.split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="text-ink-dim leading-relaxed">
              {p}
            </p>
          ))}
        </div>

        <div className="gold-hairline my-10" />

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-editorial italic text-ink-dim">
            Musesya &amp; Company Advocates — Muthaiga Square, Nairobi
          </p>
          <div className="flex items-center gap-3">
            <a
              data-testid="article-share-whatsapp"
              href={`https://wa.me/?text=${encodeURIComponent(`${article.title} — Musesya & Company Advocates\n${shareUrl}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-gold text-navy-950 text-sm font-medium px-5 py-2.5 rounded-full hover:bg-gold-hover transition-colors"
            >
              <MessageCircle size={15} /> Share on WhatsApp
            </a>
            <button
              data-testid="article-copy-link"
              onClick={copyLink}
              className="flex items-center gap-2 border border-gold/40 text-gold text-sm px-5 py-2.5 rounded-full hover:bg-gold/10 transition-colors"
            >
              <Link2 size={15} /> Copy Link
            </button>
          </div>
        </div>
      </motion.main>
    </div>
  );
}
